import * as React from "react";
import { debounce } from "lodash";
import { useState, useCallback, useRef, useEffect } from "react";
import useMobileBreakpoint from "@src/hooks/useMobileBreakpoint";
import useSearchSpring from "@src/hooks/useSearchSpring";
import ExtendedHeadings from "../../elements/ExtendedHeadings/ExtendedHeadings";
import ExtendedProductTile from "@stories/components/ProductTile/ProductTile";
import { trackProductClick, trackAutocomplete, trackSearch, trackProductImpression } from "../../../../js/searchspringBeacon";

export const HeaderSearch = ({ settings }) => {
  const [currentView, setCurrentView] = useState("product");
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const isMobile = useMobileBreakpoint("1024px");

  const { loading, autocompleteSearch, searchResults, searchResultsFacets, suggestResults, trendingResults, clearSearchResults, toggleSearchBar } = useSearchSpring(null);
  const { id } = settings.section;

  const debouncedSearch = useRef(
    debounce((value) => {
      autocompleteSearch(value);
      setIsSearchOpen(true);
    }, 300)
  ).current;

  // Track impressions when search results load
  useEffect(() => {
    if (searchResults && searchResults.length > 0 && searchQuery.length > 2) {
      console.log(`Tracking ${searchResults.length} header search result impressions for query: "${searchQuery}"`);
      
      searchResults.slice(0, 4).forEach((product, index) => {
        trackProductImpression(product, 'search_autocomplete', {
          position: index + 1,
          query: searchQuery,
          totalResults: searchResults.length,
          resultType: 'autocomplete',
          placement: 'header_search'
        });
      });
    }
  }, [searchResults, searchQuery]);

  const handleInputSearch = (value) => {
    const isValidSearch = /^[a-zA-Z0-9\s]*$/.test(value);
    if (!isValidSearch) {
      setSearchQuery(value);
      clearSearchResults();
      setIsSearchOpen(true);
      return;
    }

    setSearchQuery(value);
    
    // Track search if query is long enough
    if (value.length > 2) {
      trackSearch(value, 0, {
        type: 'autocomplete',
        placement: 'header_search'
      });
    }
    
    debouncedSearch(value);
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    setIsSearchOpen(false);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      if (searchQuery.trim()) {
        trackSearch(searchQuery, searchResults.length, {
          type: 'direct_search',
          placement: 'header_search',
          action: 'enter_key'
        });
      }
      window.location.href = `/search?q=${encodeURIComponent(searchQuery)}`;
    }
  };

  const handleCloseSearch = () => {
    const closeSearch = new CustomEvent('close-header-search', {});
    window.dispatchEvent(closeSearch);
    setSearchQuery("");
    clearSearchResults();
  };

  const handleFocus = () => {
    setIsFocused(true);
    toggleSearchBar(true);
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  // Handle suggestion clicks with tracking
  const handleSuggestionClick = useCallback((suggestion, suggestionType = 'suggested') => {
    console.log('Header search suggestion clicked:', { suggestion, originalQuery: searchQuery, suggestionType });
    
    trackAutocomplete(searchQuery, suggestion, {
      suggestionType,
      placement: 'header_search',
      position: 0
    });

    window.location.href = `/search?q=${encodeURIComponent(suggestion)}`;
  }, [searchQuery]);

  // Handle trending query clicks with tracking
  const handleTrendingClick = useCallback((trendingQuery) => {
    console.log('Header trending query clicked:', trendingQuery);

    trackAutocomplete('', trendingQuery, {
      suggestionType: 'trending',
      placement: 'header_search',
      position: 0
    });

    window.location.href = `/search?q=${encodeURIComponent(trendingQuery)}`;
  }, []);

  // Handle product clicks with tracking
  const handleProductClick = useCallback((product, position) => {
    console.log('Header search product clicked:', { product: product.title, position, searchQuery });
    
    trackProductClick(product, 'search_autocomplete', {
      position: position + 1,
      query: searchQuery,
      totalResults: searchResults.length,
      placement: 'header_search'
    });

    window.location.href = `/products/${product.handle}`;
  }, [searchQuery, searchResults.length]);

  // Handle "View All Results" click with tracking
  const handleViewAllResults = useCallback(() => {
    trackSearch(searchQuery, searchResults.length, {
      type: 'view_all_results',
      placement: 'header_search',
      action: 'view_all_button'
    });

    window.location.href = `/search?q=${encodeURIComponent(searchQuery)}`;
  }, [searchQuery, searchResults.length]);

  const showTrending = isMobile ? trendingResults?.length > 0 && searchQuery.length > 0 : trendingResults?.length > 0;
  const showClose = isMobile ? searchQuery.length > 0 : true;
  const showResults = isMobile? isSearchOpen && searchQuery.length > 0 : isSearchOpen;

    
  useEffect(() => {  
    if (searchQuery.length > 0 && isMobile) {
     document.body.classList.add('overflow-hidden');
    }
  },[searchQuery, isMobile]);


  return (
    <div className="relative header-search bg-accent">
      <div className="container px-0 lg:px-3 lg:py-8 mx-auto">
        <div className="relative header-search-bar">
          <input
            type="text"
            name="q"
            value={searchQuery}
            onChange={(e) => handleInputSearch(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={handleFocus}
            onBlur={handleBlur}
            placeholder="Search"
            className="w-full font-[#323D3E] leading-[28px] lg:leading-[24px] text-[16px] py-2 pl-10 pr-10 border border-gray-300 rounded-none lg:rounded-md"
            autoComplete="off"
            data-search-input
          />
          <i className="absolute !text-[26px] left-[0.65rem] text-xl transform -translate-y-1/2 top-1/2 icon-search text-[#748B85]"></i>

          {searchQuery && (
            <button
              onClick={handleClearSearch}
              className="absolute hidden transform -translate-y-1/2 top-1/2 -right-3"
            >
              <i className="text-xs icon-close text-[#748B85]"></i>
            </button>
          )}
          {showClose && (
          <button
            onClick={handleCloseSearch}
            className="absolute w-auto transform -translate-y-1/2 top-1/2 -right-3"
          >
            <i className="text-xs icon-close left-[0.65rem] icon-close text-[#748B85]"></i>
          </button>)
          }
        </div>
      
        {showTrending && (
          <div className="flex items-center gap-3 py-5 slide-fade-in-element px-3 lg:px-0">
            <ExtendedHeadings
              text={'Trending:'}
              type={"h4"}
              classes={`text-tertiary !text-sm`}
            />
            <div className="flex gap-4 overflow-x-auto whitespace-nowrap">
              {trendingResults.map((result, index) => (
                <a
                  href={`/search?q=${encodeURIComponent(result.searchQuery)}`}
                  key={index}
                  className="text-sm font-body text-primary hover:text-highlight"
                  onClick={(e) => {
                    e.preventDefault();
                    handleTrendingClick(result.searchQuery);
                  }}
                >
                  {result.searchQuery}
                </a>
              ))}
            </div>
          </div>
        )}

        {showResults && (
          <div className="slide-fade-in-element px-3 lg:px-0">
            <div className="w-full">
              {loading ? (
                <div className="flex items-center justify-center my-4 pb-5 lg:pb-0">
                  <div className="w-6 h-6 border-t-4 border-b-4 rounded-full border-primary animate-spin"></div>
                </div>
              ) : searchResults.length === 0 && searchQuery ? (
                <div className="py-5 text-sm text-left text-primary font-body slide-fade-in-element">
                  No results found for "<strong>{searchQuery}</strong>". Please try another search.
                </div>
              ) : (
                <div className={`flex w-full gap-4 lg:flex-row lg:gap-10 ${Object.keys(suggestResults).length > 0 ? 'py-5' : ''}`}>
                  {suggestResults && (
                    <div className="hidden lg:block lg:w-2/12">
                      <div>
                        {suggestResults.suggested?.text && (
                          <div className="mb-4 lg:mb-6">
                            <ExtendedHeadings
                              text={'Suggestions:'}
                              type={"h4"}
                              classes={`text-tertiary !text-sm mb-2`}
                            />
                            <div>
                              <a
                                href={`/search?q=${encodeURIComponent(suggestResults.suggested.text)}`}
                                className="text-sm/6 font-body text-primary hover:text-highlight"
                                onClick={(e) => {
                                  e.preventDefault();
                                  handleSuggestionClick(suggestResults.suggested.text, 'suggested');
                                }}
                              >
                                {suggestResults.suggested.text}
                              </a>
                            </div>
                          </div>
                        )}

                        {suggestResults.alternatives?.length > 0 && (
                          <div className="mb-4 lg:mb-6">
                            <ExtendedHeadings
                              text={'Did you mean:'}
                              type={"h4"}
                              classes={`text-tertiary !text-sm mb-2`}
                            />
                            <div>
                              {suggestResults.alternatives.map((alternative, index) => (
                                <a
                                  href={`/search?q=${encodeURIComponent(alternative.text)}`}
                                  key={index}
                                  className="text-sm/6 font-body text-primary hover:text-highlight"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    handleSuggestionClick(alternative.text, 'alternative');
                                  }}
                                >
                                  {alternative.text}
                                </a>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      {searchResultsFacets && (
                        <div>
                          {searchResultsFacets
                            .filter(facet => facet.field === 'variant_tog' || facet.field === 'product_type' || facet.field === 'collection_name')
                            .map((facet, index) => (
                              <div key={index} className="mb-4 lg:mb-6">
                                <ExtendedHeadings
                                  text={`${facet.label}:`}
                                  type={"h4"}
                                  classes={`text-tertiary !text-sm mb-2`}
                                />
                                <ul>
                                  {facet.values.slice(0, 5).map((value, idx) => (
                                    <li key={idx} className="text-sm/6 text-primary hover:text-highlight">
                                      <a
                                        href={`/search?q=${encodeURIComponent(searchQuery)}&filter.${facet.field}=${encodeURIComponent(value.value)}`}
                                        className="text-sm/6 font-body text-primary hover:text-highlight"
                                      >
                                        {value.label} ({value.count})
                                      </a>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            ))}
                        </div>
                      )}
                    </div>
                  )}

                  <div className="w-full">
                    <div
                      id="product-grid"
                      className={`overflow-auto max-h-[50dvh] lg:max-h-max grid grid-cols-2 lg:grid-cols-4 gap-x-[20px] lg:gap-x-[24px] gap-y-[24px] lg:gap-y-[24px] auto-cols-max slide-fade-in-element`}
                      data-id={id}
                    >
                      {searchResults?.slice(0, 4).map((result, index) => (
                        <React.Fragment key={result.id}>
                          <div 
                            className="cursor-pointer product-grid__inner slide-fade-in-element"
                            onClick={() => handleProductClick(result, index)}
                          >
                            <ExtendedProductTile
                              product={result}
                              badges={result.tags}
                              tog={result.productTogs}
                              product_swatches={result.colorPatterns}
                              source={'searchspring'}
                              image_placeholder={settings.globals.product_img_placeholder}
                              view={currentView}
                            />
                          </div>
                        </React.Fragment>
                      ))}
                    </div>

                    {searchResults?.length > 0 && !loading && (
                      <div className="mt-8 text-center">
                        <button
                          onClick={handleViewAllResults}
                          className="inline-block px-6 py-3 text-base uppercase font-body btn-primary hover:no-underline hover:!bg-accent lg:text-center"
                        >
                          View All Results
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HeaderSearch;